/* Poster Architecture V4 — Platform → Systems → Modules → Capabilities. HTML/SVG éditable, zéro image. */
import { Handshake, CandlestickChart, ShieldCheck, Network, BrainCircuit, Bot, Lock, MessagesSquare, Database } from "lucide-react";
import { useTranslation } from "react-i18next";

const SYSTEMS = [
  {
    key: "deal", name: "Deal System", color: "#EF9F27",
    tagline: "Originer, structurer, décider, transmettre.", tagline_en: "Originate, structure, decide, transmit.",
    icon: Handshake,
    modules: [
      { name: "Deal", caps: "Vue d'ensemble · Attention · Prochaines actions", caps_en: "Overview · Attention · Next actions" },
      { name: "Pipeline", caps: "Phases · Kanban · Transitions journalisées", caps_en: "Phases · Kanban · Logged transitions" },
      { name: "Deal 360", caps: "Parties · Documents · Score · Événements", caps_en: "Parties · Documents · Score · Events" },
      { name: "Origination & Leads", caps: "Sourcing · Qualification · Radar", caps_en: "Sourcing · Qualification · Radar" },
      { name: "Négociations & Courtage", name_en: "Negotiations & Brokerage", caps: "Positions · Contreparties · Mandats", caps_en: "Positions · Counterparties · Mandates" },
      { name: "Décisions", name_en: "Decisions", caps: "Scénarios sourcés · EV / AdjEV · Verdict tracé", caps_en: "Sourced scenarios · EV / AdjEV · Traced verdict" },
    ],
  },
  {
    key: "fm", name: "Financial Markets", color: "#43C6D8",
    tagline: "Observer, estimer, exécuter, apprendre.", tagline_en: "Observe, estimate, execute, learn.",
    icon: CandlestickChart,
    modules: [
      { name: "Screener", caps: "Univers coté · Signaux · Scoring", caps_en: "Listed universe · Signals · Scoring" },
      { name: "Trading & Portfolio", caps: "Positions · Cycles · P&L réel", caps_en: "Positions · Cycles · Real P&L" },
      { name: "Pont décisionnel", name_en: "Decision bridge", caps: "Estimation moteur → décision snapshotée", caps_en: "Engine estimate → snapshotted decision" },
      { name: "Apprentissage", name_en: "Learning", caps: "Prédit vs réalisé · Surprise · Calibration", caps_en: "Predicted vs realized · Surprise · Calibration" },
    ],
  },
  {
    key: "gov", name: "Governance", color: "#1D9E75",
    tagline: "Extraire, valider, prouver.", tagline_en: "Extract, validate, prove.",
    icon: ShieldCheck,
    modules: [
      { name: "Intake Engine", caps: "Document → propositions · Verbatim source", caps_en: "Document → proposals · Source verbatim" },
      { name: "Revue humaine", name_en: "Human review", caps: "Accepter · Éditer · Rejeter motivé", caps_en: "Accept · Edit · Reject with reason" },
      { name: "Registre Risques & Contrôles", name_en: "Risk & Control register", caps: "Provenance · Matrice 5×5", caps_en: "Provenance · 5×5 matrix" },
      { name: "Audits & Fournisseurs", name_en: "Audits & Suppliers", caps: "Plans · Preuves · Tiers", caps_en: "Plans · Evidence · Third parties" },
    ],
  },
];

const CAPABILITIES = [
  { icon: Network, name: "Ontology", line: "Un modèle du monde, chaque objet une seule fois", line_en: "One world model, each object only once" },
  { icon: BrainCircuit, name: "Intelligence", line: "Radar · Scoring · Scénarios · Knowledge graph", line_en: "Radar · Scoring · Scenarios · Knowledge graph" },
  { icon: Bot, name: "AI & Automation", line: "Agents qui lisent, extraient, raisonnent", line_en: "Agents that read, extract, reason" },
  { icon: Lock, name: "Trust & Security", line: "Permissions · Provenance · Audit trail", line_en: "Permissions · Provenance · Audit trail" },
  { icon: MessagesSquare, name: "Communication", line: "Cercle · Échanges contextuels tracés", line_en: "Cercle · Traced contextual exchanges" },
  { icon: Database, name: "Data & Infrastructure", line: "Drive · APIs · Hébergement suisse", line_en: "Drive · APIs · Swiss hosting" },
];

const CHAIN = ["Evidence", "Belief", "Decision", "Execution", "Outcome", "Learning", "Memory"];

export const ArchitecturePosterV4 = () => {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  return (
  <div className="border border-white/10 rounded-md bg-[#07090D] p-6 sm:p-10 space-y-8" data-testid="architecture-poster-v4">
    {/* Niveau 1 — Platform */}
    <div className="text-center">
      <div className="inline-block border border-[#378ADD]/40 rounded-md px-8 py-4 bg-[#0A0E13]">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#378ADD]">Noam Platform</div>
        <div className="noam-display text-xl sm:text-2xl font-bold text-zinc-100 mt-1">One shared decision infrastructure</div>
      </div>
      <svg className="mx-auto mt-2" width="500" height="28" viewBox="0 0 500 28" aria-hidden="true">
        <path d="M250 0 V10 M250 10 H60 M250 10 H440 M60 10 V28 M250 10 V28 M440 10 V28" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="none" />
      </svg>
    </div>

    {/* Niveau 2 & 3 — Systems → Modules → Capabilities */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {SYSTEMS.map((s) => (
        <div key={s.key} className="border rounded-md bg-[#0A0E13] overflow-hidden" style={{ borderColor: `${s.color}44` }} data-testid={`poster-system-${s.key}`}>
          <div className="px-4 py-3 border-b border-white/5" style={{ background: `${s.color}0D` }}>
            <div className="flex items-center gap-2">
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
              <span className="text-sm font-semibold text-zinc-100">{s.name}</span>
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{en ? s.tagline_en : s.tagline}</div>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600">Modules</div>
            {s.modules.map((m) => (
              <div key={m.name} className="border border-white/5 rounded px-3 py-2 bg-white/[0.02]">
                <div className="text-xs font-medium text-zinc-200">{en ? (m.name_en || m.name) : m.name}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{en ? m.caps_en : m.caps}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Chaîne décisionnelle transverse */}
    <div className="border border-white/10 rounded-md px-4 py-4 bg-[#0A0E13]">
      <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 text-center mb-3">{en ? "The chain Noam preserves: immutable, sourced, queryable" : "La chaîne que Noam conserve : immuable, sourcée, interrogeable"}</div>
      <div className="flex flex-wrap items-center justify-center gap-y-2" data-testid="poster-decision-chain">
        {CHAIN.map((step, i) => (
          <span key={step} className="flex items-center">
            <span className="text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded border border-[#43C6D8]/30 text-zinc-200 bg-[#43C6D8]/5">{step}</span>
            {i < CHAIN.length - 1 && <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true"><path d="M0 5 H16 M12 1 L17 5 L12 9" stroke="rgba(67,198,216,0.5)" strokeWidth="1.2" fill="none" /></svg>}
          </span>
        ))}
      </div>
    </div>

    {/* Niveau 4 — Capabilities partagées */}
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 text-center mb-3">{en ? "Shared capabilities, consumed by each System in its own context" : "Capabilities partagées, consommées par chaque System dans son contexte"}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {CAPABILITIES.map((c) => (
          <div key={c.name} className="border border-white/5 rounded px-3 py-2.5 bg-white/[0.02] text-center" data-testid={`poster-capability-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <c.icon className="w-3.5 h-3.5 mx-auto text-[#378ADD]" />
            <div className="text-[10px] font-semibold text-zinc-200 mt-1.5">{c.name}</div>
            <div className="text-[9px] text-zinc-600 mt-0.5 leading-snug">{en ? c.line_en : c.line}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="text-center text-[10px] uppercase tracking-[0.35em] text-zinc-600">
      Platform → Systems → Modules → Capabilities
    </div>
  </div>
  );
};
